import { type Blocker, type FacilityCycle, type Invoice } from './home.types';

const MOCK_OFFICES = [
  { id: 'off-1', name: 'MedPlus San Juan', address: '123 Main St' },
  { id: 'off-2', name: 'MedPlus Bayamón', address: '45 Plaza Ave' },
  { id: 'off-3', name: 'Care Center Ponce', address: '88 Aurora St' },
] as const;

const MOCK_PATIENTS = [
  'Maria Lopez',
  'Carlos Rivera',
  'Ana Torres',
  'Gustavo Fuentes',
  'Sofia Delgado',
  'Luis Mendez',
  'Elena Ruiz',
  'Pedro Vargas',
  'Camila Nieves',
  'Diego Ortiz',
  'Isabel Cruz',
  'Ricardo Morales',
] as const;

const MOCK_FACILITIES = [
  'Hall Of Justice',
  'MedPlus Bayamón',
  'Care Center San Juan',
  'Metro Health Plaza',
  'Northside Clinic',
  'Coastal Care Center',
  'Valley Medical Hub',
  'Sunrise Facility',
  'Harbor View Center',
  'Central Wellness',
  'Eastside Recovery',
  'Lakeside Care',
] as const;

const MOCK_BLOCKER_STEPS = [
  { stepName: 'Patient address missing', resolveType: 'needs_information', activityType: 'intake' },
  {
    stepName: 'Referral documentation missing',
    resolveType: 'add_document',
    activityType: 'document',
  },
  {
    stepName: 'Prescription awaiting approval',
    resolveType: 'needs_review',
    activityType: 'review',
  },
  {
    stepName: 'Insurance authorization pending',
    resolveType: 'needs_information',
    activityType: 'insurance',
  },
  { stepName: 'Consent form unsigned', resolveType: 'add_document', activityType: 'document' },
  { stepName: 'Lab results pending review', resolveType: 'needs_review', activityType: 'review' },
  {
    stepName: 'Medication list incomplete',
    resolveType: 'needs_information',
    activityType: 'intake',
  },
  { stepName: 'Prior auth expired', resolveType: 'needs_information', activityType: 'insurance' },
  { stepName: 'Discharge summary missing', resolveType: 'add_document', activityType: 'document' },
  { stepName: 'Care plan needs approval', resolveType: 'needs_review', activityType: 'review' },
  {
    stepName: 'Emergency contact required',
    resolveType: 'needs_information',
    activityType: 'intake',
  },
  {
    stepName: 'Coverage verification failed',
    resolveType: 'needs_information',
    activityType: 'insurance',
  },
] as const;

function addDays(base: string, days: number): string {
  const date = new Date(base);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

/** UI supplement data merged with API responses in non-production. */
export const MOCK_INVOICES: readonly Invoice[] = MOCK_PATIENTS.map((patientName, index) => {
  const office = MOCK_OFFICES[index % MOCK_OFFICES.length];
  const patientId = 10 + index;

  return {
    id: 1001 + index,
    patientId,
    patientName,
    dueDate: index === 0 ? new Date().toISOString().slice(0, 10) : addDays('2026-08-01', index * 3),
    created: addDays('2026-07-15', index * 2),
    dueAmount: Math.round((89 + index * 37.5) * 100) / 100,
    status: 'open',
    officeId: office.id,
    officeName: office.name,
    officeAddress: office.address,
    paymentStatus: index % 4 === 0 ? 'overdue' : 'pending',
    isFinanciallyResponsible: index % 3 !== 0,
  };
});

export const MOCK_FACILITY_CYCLES: readonly FacilityCycle[] = MOCK_FACILITIES.map(
  (facilityName, index) => ({
    id: 501 + index,
    facilityName,
    cycleBegin: addDays('2026-11-01', index * 7),
    cycleEnd: addDays('2026-11-15', index * 7),
    cycleDelivery: addDays('2026-11-13', index * 7),
    censusCount: (index % 5) + 2,
    invoicesCount: index % 4,
    changesCount: (index % 3) + 1,
    risksCount: index % 5,
    checkedIn: index % 4 === 2,
  }),
);

export const MOCK_BLOCKERS: readonly Blocker[] = MOCK_BLOCKER_STEPS.map((step, index) => {
  const patientName = MOCK_PATIENTS[index % MOCK_PATIENTS.length];
  const patientId = 20 + (index % MOCK_PATIENTS.length);

  return {
    id: index + 1,
    patientId,
    patientName,
    stepName: step.stepName,
    stepOrder: (index % 3) + 1,
    description: `${step.stepName} for ${patientName}. Action required to continue care coordination.`,
    created: addDays('2026-04-01', index * 4),
    activityId: `act-${index + 1}`,
    activityType: step.activityType,
    resolveType: step.resolveType,
    formSchema: '',
  };
});

/** Documents awaiting review — used when the count API is empty or unavailable in non-prod. */
export const MOCK_DOCUMENTS_REVIEW_COUNT = 8;
